import React from 'react';
import { motion, Variants } from 'framer-motion';

interface SlideUpProps {
  children: React.ReactNode;
  /** Delay before animation starts (in seconds) */
  delay?: number;
  /** Distance to slide from (in pixels) */
  distance?: number;
  /** Use spring physics for bounce effect */
  spring?: boolean;
  /** Animation duration (in seconds, ignored if spring is true) */
  duration?: number;
  /** Custom className for styling */
  className?: string;
}

/**
 * SlideUp - Slide up with fade animation
 *
 * Animates content from below (with opacity 0) to its final position.
 * Supports both smooth easing and spring physics for bounce effect.
 * Respects prefers-reduced-motion accessibility preference.
 *
 * @example
 * ```tsx
 * // Smooth slide
 * <SlideUp delay={0.1} distance={30}>
 *   <Card />
 * </SlideUp>
 *
 * // Spring bounce effect
 * <SlideUp spring>
 *   <Card />
 * </SlideUp>
 * ```
 */
export const SlideUp: React.FC<SlideUpProps> = ({
  children,
  delay = 0,
  distance = 20,
  spring = false,
  duration = 0.4,
  className,
}) => {
  const variants: Variants = {
    hidden: {
      opacity: 0,
      y: distance,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: spring
        ? {
            type: 'spring',
            stiffness: 200,
            damping: 20,
            delay,
          }
        : {
            duration,
            delay,
            ease: [0.4, 0.0, 0.2, 1], // Material Design easing
          },
    },
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={variants}
      className={className}
      style={{ willChange: 'transform, opacity' }}
    >
      {children}
    </motion.div>
  );
};
